/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse } from '@angular/common/http';
import { afterNextRender, Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY } from 'rxjs';
import { getGroupRoleTypeLabel, GroupRoleTypeEnum } from '../../constants/group-role.constant';
import { ConfirmationPopupComponent } from '../../libraries/popup/components/confirmation-popup/confirmation-popup.component';
import { SuccessPopupComponent } from '../../libraries/popup/components/success-popup/success-popup.component';
import { IUserModel } from '../../models/user.model';
import { GroupsService } from '../../services/api/groups/groups.service';
import { UserService } from '../../services/api/user/user.service';
import { GroupContextService } from '../../services/helper/group-context/group-context.service';
import { LoaderService } from '../../services/loader/loader.service';
import { StorageService } from '../../services/storage/storage.service';

@Component({
    selector: 'app-people',
    standalone: false,
    templateUrl: './people.component.html',
    styleUrl: './people.component.scss',
})
export class PeopleComponent {
    @Output() personClick = new EventEmitter<void>();
    @Input() isAdminModerator = false;
    @Input() isFollowRequest = false;
    @Input() isGroupMember = false;
    @Input() isJoinRequest = false;
    protected groupId!: number;
    protected currentLoginUserId = this.storageService.getLoginUser()?.id;
    protected isOwnGroup = signal<boolean>(false);
    protected isGroupMemberSignal = signal<boolean>(false);
    protected peopleSignal = signal<IUserModel>({} as IUserModel);
    protected GroupRoleTypeEnum = GroupRoleTypeEnum;
    protected getGroupRoleTypeLabel = getGroupRoleTypeLabel;

    constructor(
        private dialog: MatDialog,
        private groupContextService: GroupContextService,
        private groupsService: GroupsService,
        private loaderService: LoaderService,
        private storageService: StorageService,
        private userService: UserService,
    ) {
        afterNextRender(() => {
            this.groupContextService.groupDetails$.subscribe((details) => {
                this.groupId = details.id;
                this.isOwnGroup.set(
                    details.role === GroupRoleTypeEnum.Owner && this.currentLoginUserId === this.peopleSignal().id,
                );
            });
        });
    }

    @Input() set people(value: IUserModel) {
        this.peopleSignal.set(value);
    }

    protected onPersonClick(): void {
        this.personClick.emit();
    }

    protected acceptFollowRequest(): void {
        this.loaderService.show();
        this.userService.acceptFollowRequest(this.peopleSignal().id).subscribe({
            next: () => {
                this.peopleSignal.update((p) => ({
                    ...p,
                    followed: true,
                }));
                const popupConfig = SuccessPopupComponent.getDialogConfig();
                popupConfig.data = {
                    title: 'New Connection!',
                    message: "You've successfully welcomed a new follower to your journey.",
                    iconPath: '/assets/images/shared/success-green-icon.svg',
                    confirmButtonLabel: 'Great',
                };
                const dialogRef = this.dialog.open(SuccessPopupComponent, popupConfig);
                dialogRef.afterClosed().subscribe(() => {
                    this.userService.followRequestSubject.next();
                });
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Accept follow request failed', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    protected rejectFollowRequest(): void {
        const popupConfig = ConfirmationPopupComponent.getDialogConfig();
        popupConfig.data = {
            title: 'Decline Follower?',
            message: 'Are you sure you want to decline this user from following you?',
            iconPath: '/assets/images/shared/block-user-grey-icon.svg',
            confirmButtonLabel: 'Decline',
        };
        const dialogRef = this.dialog.open(ConfirmationPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe({
            next: (result: boolean) => {
                if (!result) {
                    return;
                }
                this.loaderService.show();
                this.userService.rejectFollowRequest(this.peopleSignal().id).subscribe({
                    next: () => {
                        this.userService.followRequestSubject.next();
                    },
                    error: (error) => {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error rejecting follow request:', error);
                        return EMPTY;
                    },
                    complete: () => {
                        this.loaderService.hide();
                    },
                });
            },
        });
    }

    protected approveMemberJoinRequest(): void {
        this.loaderService.show();
        this.groupsService.approveMemberJoinGroup(this.groupId, this.peopleSignal().id).subscribe({
            next: () => {
                this.groupContextService.notifyMemberJoinRequestListUpdated();
                this.groupContextService.notifyMemberListUpdated();
                const popupConfig = SuccessPopupComponent.getDialogConfig();
                popupConfig.data = {
                    title: 'Community Growing!',
                    message: 'You have successfully welcomed this member into the group.',
                    iconPath: '/assets/images/shared/success-green-icon.svg',
                    confirmButtonLabel: 'Great',
                };
                this.dialog.open(SuccessPopupComponent, popupConfig);
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error approving member:', error);
                return EMPTY;
            },
            complete: () => this.loaderService.hide(),
        });
    }

    protected openRejectMemberJoinRequestPopup(): void {
        const popupConfig = ConfirmationPopupComponent.getDialogConfig();
        popupConfig.data = {
            title: 'Decline Request?',
            message: "Are you sure you want to decline this person's request to join the group?",
            iconPath: '/assets/images/shared/block-user-grey-icon.svg',
            confirmButtonLabel: 'Decline',
        };
        const dialogRef = this.dialog.open(ConfirmationPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe({
            next: (result: boolean) => {
                if (!result) {
                    return;
                }
                this.loaderService.show();
                this.groupsService.rejectMemberJoinGroup(this.groupId, this.peopleSignal().id).subscribe({
                    next: () => {
                        this.groupContextService.notifyMemberJoinRequestListUpdated();
                    },
                    error: (error) => {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error rejecting member:', error);
                        return EMPTY;
                    },
                    complete: () => {
                        this.loaderService.hide();
                    },
                });
            },
        });
    }

    protected getRoleActions(): {
        icon: string;
        alt: string;
        text: string;
        click: () => void;
    }[] {
        const role = this.peopleSignal().role;
        const actions = [];

        if (role === GroupRoleTypeEnum.Member) {
            actions.push({
                icon: '/assets/images/shared/crown-grey-icon.svg',
                alt: 'Crown',
                text: 'Assign as Admin',
                click: () => this.assignRole(GroupRoleTypeEnum.Admin),
            });
        }
        if ([GroupRoleTypeEnum.Admin, GroupRoleTypeEnum.Member].includes(role)) {
            actions.push({
                icon: '/assets/images/shared/shield-grey-icon.svg',
                alt: 'Shield',
                text: 'Assign as Moderator',
                click: () => this.assignRole(GroupRoleTypeEnum.Moderator),
            });
        }
        if (role === GroupRoleTypeEnum.Moderator) {
            actions.push({
                icon: '/assets/images/shared/crown-grey-icon.svg',
                alt: 'Crown',
                text: 'Promote to Admin',
                click: () => this.assignRole(GroupRoleTypeEnum.Admin),
            });
        }
        if ([GroupRoleTypeEnum.Admin, GroupRoleTypeEnum.Moderator].includes(role)) {
            actions.push({
                icon: '/assets/images/shared/people-icon.svg',
                alt: 'People',
                text: 'Demote to Member',
                click: () =>
                    this.demoteMember(
                        role === GroupRoleTypeEnum.Moderator ? GroupRoleTypeEnum.Moderator : GroupRoleTypeEnum.Admin,
                    ),
            });
        }

        return actions;
    }

    protected assignRole(role: GroupRoleTypeEnum): void {
        const roleActions: any = {
            [GroupRoleTypeEnum.Admin]: {
                serviceCall: () => this.groupsService.assignAdmin(this.groupId, this.peopleSignal().id),
                title: 'Leadership Updated',
                message: 'You have successfully entrusted this member with Admin responsibilities.',
            },
            [GroupRoleTypeEnum.Moderator]: {
                serviceCall: () => this.groupsService.assignModerator(this.groupId, this.peopleSignal().id),
                title: 'Leadership Updated',
                message: 'You have successfully entrusted this member with Moderator responsibilities.',
            },
        };

        const action = roleActions[role];
        if (!action) {
            return;
        }

        this.loaderService.show();
        action.serviceCall().subscribe({
            next: () => {
                // Update local signal
                this.peopleSignal.update((p) => ({ ...p, role }));
                // Show success popup
                const popupConfig = SuccessPopupComponent.getDialogConfig();
                popupConfig.data = {
                    title: action.title,
                    message: action.message,
                    iconPath: '/assets/images/shared/success-green-icon.svg',
                };
                this.dialog.open(SuccessPopupComponent, popupConfig);
            },
            error: (error: HttpErrorResponse) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error assigning role:', error);
            },
            complete: () => this.loaderService.hide(),
        });
    }

    protected demoteMember(role: GroupRoleTypeEnum): void {
        const roleActions: any = {
            [GroupRoleTypeEnum.Admin]: {
                serviceCall: () => this.groupsService.demoteAdmin(this.groupId, this.peopleSignal().id),
                title: 'Role Updated',
                message: 'You have successfully transitioned this Admin back to a regular member role.',
            },
            [GroupRoleTypeEnum.Moderator]: {
                serviceCall: () => this.groupsService.demoteModerator(this.groupId, this.peopleSignal().id),
                title: 'Role Updated',
                message: 'You have successfully transitioned this Moderator back to a regular member role.',
            },
        };

        const action = roleActions[role];
        if (!action) {
            return;
        }

        this.loaderService.show();
        action.serviceCall().subscribe({
            next: () => {
                // Update local signal
                this.peopleSignal.update((p) => ({ ...p, role: GroupRoleTypeEnum.Member }));
                // Show success popup
                const popupConfig = SuccessPopupComponent.getDialogConfig();
                popupConfig.data = {
                    title: action.title,
                    message: action.message,
                    iconPath: '/assets/images/shared/success-green-icon.svg',
                };
                this.dialog.open(SuccessPopupComponent, popupConfig);
            },
            error: (error: HttpErrorResponse) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error demoting member:', error);
            },
            complete: () => this.loaderService.hide(),
        });
    }

    protected openRemoveMemberPopup(): void {
        const popupConfig = ConfirmationPopupComponent.getDialogConfig();
        popupConfig.data = {
            title: 'Remove Member?',
            message: 'Are you sure you want to remove this person from the group fellowship?',
            iconPath: '/assets/images/shared/delete-account-icon.svg',
            confirmButtonLabel: 'Remove',
        };
        const dialogRef = this.dialog.open(ConfirmationPopupComponent, popupConfig);
        dialogRef.afterClosed().subscribe({
            next: (result: boolean) => {
                if (!result) {
                    return;
                }

                this.loaderService.show();
                this.groupsService.removeMember(this.groupId, this.peopleSignal().id).subscribe({
                    next: () => {
                        this.groupContextService.notifyMemberListUpdated();
                    },
                    error: (error) => {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error removing member:', error);
                        return EMPTY;
                    },
                    complete: () => {
                        this.loaderService.hide();
                    },
                });
            },
        });
    }
}
